/**
 * Break-down into the player's hold (economy plan SY-3, direction §2.6, D-TBD-c6): a salvaged component in the hold
 * is taken apart into goods, `salvageYields[type] × condition` units of each good (strategic `modules.json
 * salvageYields`, read here on the main thread). The goods go into the hold's goods block (`cargo.parts`), never into
 * the station's market: a break-down deposits nothing at the station (unlike a component sale, which the strategic
 * economy books as `playerSoldComponent`), so player break-downs remove component supply from the sim (D5).
 *
 * - **Goods flag (R3-H2, the goods deploy rule):** break-down puts goods in the hold, so it refuses while
 *   `career.json goods.tradeEnabled` is off (until the SY-2b + SY-2d deploy), like `sellGood`.
 * - **Where:** at stations with a market whose M2 `prices.components` isn't null (the Hub, the Scrapyard, and every
 *   Reach station bridged from those templates).
 * - **What:** every band (a wrecked part can be broken down). Sealed, contract and story components refuse, and so
 *   does a type with no yield (a cargo pod).
 * - **Room:** it refuses when the yields don't fit the hold once the component is gone (no partial loss).
 * - **Rush spares (M8 MC-h3):** a Rush-pattern component (`provenance: 'rush'`, MC-h1) that yields anything also
 *   yields `RUSH_SPARES_PER_PART` whole `rush_spares` on top, at any condition. A yield-less type stays refused.
 *
 * Pure: Career in, Career out (`cargo.ts` is M7 S14's; this file is SY-3's).
 */
import { z } from 'zod'
import modulesJson from '../data/strategic/modules.json'
import { COMPONENT_TYPES, type ComponentType, type M2Data, type StationDef } from '../data/m2Schemas'
import { containerHoldT, goodHoldT } from './hullFit'
import { EPSILON, GOOD_NAMES, cargoUsedT, withGood, withoutComponent } from './ops'
import { GOOD_IDS, GOOD_MASS_T, goodIdSchema, type GoodId } from './parts'
import { cargoFull, fail, formatTonnes, ok, type CmdResult } from './result'
import type { CargoComponent, Career } from './types'

/** Goods per component type at condition 1 (every type listed; `{}` = nothing to recover). */
export type BreakDownYields = Readonly<Record<ComponentType, Readonly<Partial<Record<GoodId, number>>>>>

/** The yields table's schema: every component type, and only goods (a raw ware can't go into the goods block). */
export const breakDownYieldsSchema = z.record(z.enum(COMPONENT_TYPES), z.partialRecord(goodIdSchema, z.number().nonnegative()))

/** Parses a yields table (throws a ZodError naming the bad field). */
export function loadBreakDownYields(raw: unknown = modulesJson.salvageYields): BreakDownYields {
  return breakDownYieldsSchema.parse(raw)
}

/** The bundled yields (strategic `modules.json salvageYields`). */
export const BREAKDOWN_YIELDS: BreakDownYields = Object.freeze(loadBreakDownYields())

export const BREAKDOWN_TEXTS = {
  closed: 'Breaking components down is not open yet.',
  noService: '{station} does not take components apart.',
  notInCargo: 'That component is not in the hold.',
  contract: 'Contract and story cargo cannot be broken down.',
  nothing: 'There is nothing to recover from it.',
} as const

/** One good a break-down yields. */
export interface BreakDownLine {
  good: GoodId
  /** Units (`cargo-parts.json massT` tonnes each). */
  qty: number
}

/** Whole Rush spares a Rush-pattern component yields on top of its goods (the card's rule: one spare per part). */
export const RUSH_SPARES_PER_PART = 1

/**
 * The goods `component` breaks down into (in goods order; zero lines dropped). A Rush-pattern component with any
 * yield adds `RUSH_SPARES_PER_PART` `rush_spares` (the last good, so the last line).
 */
export function breakDownLines(
  component: Pick<CargoComponent, 'type' | 'condition' | 'provenance'>,
  yields: BreakDownYields = BREAKDOWN_YIELDS,
): BreakDownLine[] {
  const table = yields[component.type]
  const c = Math.min(1, Math.max(0, component.condition))
  const out: BreakDownLine[] = []
  for (const good of GOOD_IDS) {
    const qty = (table[good] ?? 0) * c
    if (qty > EPSILON) out.push({ good, qty })
  }
  if (out.length > 0 && component.provenance === 'rush') {
    const spare = out.find((l) => l.good === 'rush_spares')
    if (spare === undefined) out.push({ good: 'rush_spares', qty: RUSH_SPARES_PER_PART })
    else spare.qty += RUSH_SPARES_PER_PART
  }
  return out
}

/** "3.6 t Electronics, 1.8 t Drive Parts" (the Market's break-down row and the notice). */
export function breakDownLabel(lines: readonly BreakDownLine[]): string {
  return lines.map((l) => `${formatTonnes(l.qty * GOOD_MASS_T[l.good])} t ${GOOD_NAMES[l.good]}`).join(', ')
}

/** True when `station` takes components apart (a market, and M2 component prices). */
export function breaksDownAt(station: StationDef): boolean {
  return station.services.includes('market') && station.prices.components !== null
}

/**
 * Breaks the hold component `componentId` down at `station` into goods in the hold. Refuses (Career untouched) while
 * `goods.tradeEnabled` is off, at a station that doesn't take components apart, for a component not in the hold, a
 * sealed, contract or story component, a type with no yield, or yields that don't fit the hold.
 */
export function breakDownComponent(
  career: Career,
  data: Pick<M2Data, 'career'>,
  station: StationDef,
  componentId: string,
  yields: BreakDownYields = BREAKDOWN_YIELDS,
): CmdResult<Career> {
  if (!data.career.goods.tradeEnabled) return fail('serviceUnavailable', BREAKDOWN_TEXTS.closed)
  if (!breaksDownAt(station)) {
    return fail('serviceUnavailable', BREAKDOWN_TEXTS.noService.replace('{station}', station.name), { station: station.id })
  }
  const component = career.cargo.components.find((c) => c.id === componentId)
  if (component === undefined) return fail('notInCargo', BREAKDOWN_TEXTS.notInCargo, { id: componentId })
  if (component.sealed === true || component.contractId !== undefined || component.story === true) {
    return fail('notBoughtHere', BREAKDOWN_TEXTS.contract, { id: component.id })
  }
  const lines = breakDownLines(component, yields)
  if (lines.length === 0) return fail('notBoughtHere', BREAKDOWN_TEXTS.nothing, { id: component.id })
  let needT = 0
  // M10 P5a6b H3: class-weighted on the flown hull (the goods by class, the freed component as container; raw today).
  const defId = career.ship.defId
  for (const l of lines) needT += goodHoldT(defId, l.good, l.qty)
  const free = Math.max(0, career.cargo.capacityT - (cargoUsedT(career) - containerHoldT(defId, component.massT)))
  if (needT > free + EPSILON) return cargoFull(needT, free)
  let next = withoutComponent(career, component.id)
  for (const l of lines) next = withGood(next, l.good, l.qty)
  return ok(next)
}
